import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import './VisCAT_test.css';
import { useNavigate } from 'react-router-dom';
import PatternLock from "../components/PatternLock.jsx";
import testData from "../Viscat-test-data.json";
import TestResults from '../utils.js';
import {
  getStoredPattern,
  resolveLevel,
} from '../viscatPatterns.js';
import { testToolsEnabled } from '../testTools';

const levels = testData.levels;

// VISCAT-386 - derived, so adding a level does not silently break the total.
const MAX_SCORE = levels.filter((level) => level['mode'] !== 'practice').length * 2;

const { backEndBaseURL } = require('../config');

// Create a separate memoized component for the image
const MemoizedImage = React.memo(({ showVideo, levelAssets, levelName, toggleAudio, demoAudioRef, testAudioRef }) => {
  //console.log('Rendering image for level:', levelName);
  if (showVideo) {
    return <img src={levelAssets.message} alt={"Demo " + levelName + " Message"} onClick={() => toggleAudio(demoAudioRef)} className='Viscat_MSG' />;
  } else {
    return <img src={levelAssets.message} alt={levelName + " Message"} onClick={() => toggleAudio(testAudioRef)} className='Viscat_MSG' />;
  }
});

const VisCAT_test = () => {
  // Test grid controlers, variables and paramaters
  const navigate = useNavigate();
  const [currentLevel, setcurrentLevel] = useState(Number(localStorage.getItem("testLevel")) || 0);
  const levelDef = levels[currentLevel];
  const pattern = useMemo(() => getStoredPattern(), []);

  // we only show the video if it is not a practice level
  const [showVideo, setShowVideo] = useState(!(levelDef['mode'] === 'practice'));

  const resolved = useMemo(
    () => resolveLevel(levelDef, pattern, testData, { showVideo }),
    [levelDef, pattern, showVideo]
  );

  //erro message handling for modals
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const showErrorModal = (message) => {
    setErrorMessage(message);
    setIsModalVisible(true);
  };

  const closeModal = () => {
    setIsModalVisible(false);
    setErrorMessage('');
    localStorage.setItem('testUID', '');
    navigate('/');
  };

  // VISCAT-386 (non-production only): abandon a demo run and return to Home.
  // Clears only the demo run's own state; the formal test path is untouched.
  const exitDemoRun = () => {
    localStorage.removeItem('testLevel');
    localStorage.removeItem('patternPath');
    localStorage.removeItem('isDemoMode');
    localStorage.removeItem('isPickedPattern');
    navigate('/');
  };

  const getLevelAssets = (level) => {
    const prefix = levels[level]["asset-prefix"];
    return {
      //video: require(`../Assets/Viscat_test_graphics/${prefix}.gif`),
      demoAudio: require(`../Assets/Viscat_test_graphics/${prefix}_demo.mp3`),
      testAudio: require(`../Assets/Viscat_test_graphics/${prefix}.mp3`),
      message: require(`../Assets/Viscat_test_graphics/${prefix}_msg.png`),
    };
  };

  const [levelAssets, setLevelAssets] = useState(getLevelAssets(currentLevel));

  // Load common assets
  const commonAssets = {
    resetButton: require("../Assets/Viscat_test_graphics/reset_icon_testpage.png"),
    undoButton: require("../Assets/Viscat_test_graphics/undo_testpage.png"),
    disabledUndo: require("../Assets/Viscat_test_graphics/disabled_undo.png"),
    disabledReset: require("../Assets/Viscat_test_graphics/disabled_reset.png"),
  };

  // Test grid controlers, variables and paramaters
  const grid_size = window.innerHeight <= 820 ? "70%" : "70%";
  const point_size = "5vmin";
  const gridDimension = testData["grid-size"];
  const set_pattern = resolved.leftGrid;
  //const level = levelName;
  const [path, setPath] = useState([]);

  // Data capture for research team and database
  const [tr, setTr] = useState(new TestResults(levelDef["level"])); // tr is short hand for TestResults
  // VISCAT-428: refs, not state. A handler holding an old closure saw a stale
  // fingerDown === true and logged extra fingerUp stats (same millisecond =>
  // test_stats_pkey violation); submittingRef stops a double-tapped done! posting twice.
  const fingerDownRef = useRef(false);
  const submittingRef = useRef(false);

  // eslint-disable-next-line no-unused-vars
  const [testUID, setTestUID] = useState(localStorage.getItem("testUID"));

  /*
    VISCAT-386 - is this a demo run?

    Gated on the POSITIVE isDemoMode marker that startDemo writes, not on the
    absence of a testUID. closeModal writes an EMPTY STRING to testUID rather than
    removing the key, so `!testUID` is true for a real candidate whose session hit
    an error modal - which would show that candidate their score. goToTestingPage,
    the formal entry path, never sets isDemoMode.

    Frozen at mount, like testUID above, so a later write cannot change it mid-run.
  */
  const [isDemoRun] = useState(
    () => localStorage.getItem("isDemoMode") === "true" && !localStorage.getItem("testUID")
  );

  /*
    VISCAT-386 - was the pattern deliberately chosen from the picker?

    Narrower than isDemoRun on purpose: the score summary is shown only for a picked
    pattern, not for a random "Try the Game" run. startDemo writes this marker on
    both branches, so a random run cannot inherit it from an earlier picked one.

    The Exit control below stays on isDemoRun - it is about being able to leave any
    demo run, which is a separate concern from whether a score is shown.
  */
  const [isPickedPatternRun] = useState(
    () => localStorage.getItem("isPickedPattern") === "true"
      && localStorage.getItem("isDemoMode") === "true"
      && !localStorage.getItem("testUID")
  );

  // Scores survive level advance here because levels change via setcurrentLevel
  // without unmounting, while `tr` is replaced on each advance.
  const scoresRef = useRef([]);

  // Memoize the toggle function used by the sound button
  const memoizedToggleAudio = useCallback((audioRef) => {
    toggleAudio(audioRef);
  }, []);

  //audio functionality
  var demoAudioRef = useRef(new Audio());
  var testAudioRef = useRef(new Audio());
  // var submitAudioRef = useRef(new Audio()); 

  /*
    function designed to handle correct audio assignment for ALL
    level specific outputs
    last edited: 01/08/2024
  */
  function completeAudioSelect() {
    demoAudioRef.current.src = levelAssets.demoAudio;
    testAudioRef.current.src = levelAssets.testAudio;
  }

  const handleAudioEnded = (audioRef) => {
    audioRef.current.addEventListener('ended', () => {
      audioRef.current.currentTime = 0;
      audioRef.current.hasEndedEventListener = true;
    });
  };

  /* stops All audio*/
  function stopAllAudio() {
    stopAudio(testAudioRef);
    stopAudio(demoAudioRef);
    // stopAudio(submitAudioRef);
  };

  function stopAudio(audio) {
    if (audio && audio.current) {
      audio.current.pause();
      audio.current.currentTime = 0;
    }
  };

  // play audio from start (used by auto-play flows)
  function play(audioInput) {
    stopAllAudio();
    const audio = audioInput.current;
    audio.currentTime = 0;
    audio.play().catch(err => console.error("Error playing the audio:", err));
  };

  // toggle pause/resume on the selected audio (used by sound button)
  function toggleAudio(audioInput) {
    const audio = audioInput.current;
    const isPlaying = !audio.paused && !audio.ended && audio.currentTime > 0;

    if (isPlaying) {
      audio.pause();
      return;
    }

    // ensure only the current instruction audio is active at a time
    if (audioInput === demoAudioRef) {
      stopAudio(testAudioRef);
    } else {
      stopAudio(demoAudioRef);
    }

    audio.play().catch(err => console.error("Error playing the audio:", err));
  }


  useEffect(() => {
    const demo = demoAudioRef.current;
    const test = testAudioRef.current;
    return () => {
      demo.pause();
      demo.currentTime = 0;
      test.pause();
      test.currentTime = 0;
    };
  }, []);

  useEffect(() => {
    handleAudioEnded(demoAudioRef);
    handleAudioEnded(testAudioRef);
  }, []);

  useEffect(() => {
    completeAudioSelect();
    if (showVideo) {
      play(demoAudioRef);
    } else {
      play(testAudioRef);
    }
    console.log('useEffect.setLevelAssets.' + currentLevel);
  }, [currentLevel, showVideo]);


  const handleSubmit = () => {
    handleFinalSubmit();
    stopAllAudio();
  };

  // // large if conditions, playing audio based on level conditions
  // const handleImages = () => {
  //   console.log( 'handleImages.level=', levelName);
  //   if ( showVideo ) {
  //     return <img src={levelAssets.message} alt={"Demo "+levelName+" Message"} onClick={() => play(demoAudioRef)} className='Viscat_MSG'/>;
  //   } else {
  //     return <img src={levelAssets.message} alt={levelName+" Message"} onClick={() => play(testAudioRef)} className='Viscat_MSG'/>;
  //   }
  // };

  /*
    Function requests to post users' test results (tr) to database, fundamental to testing.
    Currently opperates to take no paramaters working with the local storage of testUID. 
    last edited: 31/07/2024
  */
  async function postData() {
    // Checks local storage testUID, if exists proceeds to try and post data
    // only post if it is not a practice level
    let result = null;
    if (testUID && levelDef['mode'] !== 'practice') {
      try {
        // console.log('sending data', JSON.stringify(tr,null,2));
        console.log(`${backEndBaseURL}/v2/candidateTest/${testUID}`);
        result = await fetch(`${backEndBaseURL}/v2/candidateTest/${testUID}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ ...tr, patternPath: pattern })

        });

        // console.log(result);
        // Check if response status is invalid 
        if (!result.ok) {
          // throw new Error(`HTTP error! status: ${result.status}`);
          console.error('Failed to post data:', result);
        }
      } catch (error) {
        console.error(error);
      }
    }
    return result;
  }

  /*
    Function works to evaluate results of current level, comparing input to the 
    desired result. This function will determine the next course of user actions, 
    preparing the necessary scripts. Additionaly the function will  call the postData()
    operation, ensuring all caputred data is forwarded to the remote DataBase via 
    the back-end API.
    last edited: 31/07/2024
  */
  async function handleFinalSubmit() {
    if (submittingRef.current) return;
    submittingRef.current = true;
    try {
      await submitLevel();
    } finally {
      submittingRef.current = false;
    }
  }

  async function submitLevel() {
    tr.addState('submit');
    handleFinish();
    toggleVideoGrid();

    const twoPointCorrectAnswer = resolved.answer;
    const onePointCorrectAnswer = resolved.answer.slice(0, resolved.onePointAnswerPos);

    // console.log( 'correct: ' +JSON.stringify(onePointCorrectAnswer));
    // console.log( 'student: ' +JSON.stringify(path.slice(0, onePointCorrectAnswer.length)));

    var gotoNextLevel = false;

    var postResult = null;

    if (levelDef['mode'] !== 'practice') {
      // we score and do normal things
      if (path.length === twoPointCorrectAnswer.length && JSON.stringify(path) === JSON.stringify(twoPointCorrectAnswer)) {
        // we have a correct path so set a pass
        tr.setScore(2);
        // console logs data and posts it to database
        postResult = await postData();

        // test if the we have done all three levels yet.
        if (currentLevel < levels.length - 1) {
          gotoNextLevel = true;
        }
      } else if (path.length >= onePointCorrectAnswer.length
        && JSON.stringify(path.slice(0, onePointCorrectAnswer.length)) === JSON.stringify(onePointCorrectAnswer)) {
        // we have a correct path to one point
        tr.setScore(1);
        // console logs data and posts it to database
        postResult = await postData();
      } else {
        // pattern did not match therefore score of 0
        tr.setScore(0);
        // console.log( 'correct: ' +JSON.stringify(onePointCorrectAnswer));
        // console.log( 'student: ' + JSON.stringify(path));
        postResult = await postData();
      }

      // VISCAT-386 (non-production, demo runs only). One push site reading the
      // score the branches above just set, so the three setScore calls cannot
      // drift apart. Inside the non-practice branch, so practice contributes no
      // row - it has no denominator in MAX_SCORE.
      if (testToolsEnabled() && isPickedPatternRun) {
        scoresRef.current.push({ level: levelDef['level'], score: tr.score });
      }

      // Demo flow has no testUID and intentionally skips backend submission.
      // Only treat missing/failed post as an error in real candidate mode.
      if (testUID && (!postResult || !postResult.ok)) {
        console.error('Failed to post data', postResult);
        showErrorModal('There was an issue submitting your results. Please ask for assistance.  '
          + (postResult ? ` (Error code: ${postResult.status})` : '')
          + (postResult ? ` URL: ${postResult.url}` : 'N/A'));
        return;
      }
    } else {
      // its a practice level and we dont score and dont submit
      if (currentLevel < levels.length - 1) {
        gotoNextLevel = true;
      }
    }

    if (gotoNextLevel) {
      // we are not at the next level so move there and reset TestResults
      setcurrentLevel(currentLevel + 1);
      setLevelAssets(getLevelAssets(currentLevel + 1));
      localStorage.setItem("testLevel", Number(currentLevel) + 1);
      // console.log(tests[currentLevel + 1]);
      setTr(new TestResults(levels[currentLevel + 1]["level"]));
      setPath([]);
    } else {
      // VISCAT-386: the WRITE is gated, not just the render. Outside a non-production
      // demo run this reduces to the original single-argument navigate, so no score is
      // ever assembled, passed or persisted.
      const demoSummary = (testToolsEnabled() && isPickedPatternRun)
        ? { scores: scoresRef.current, maxScore: MAX_SCORE }
        : undefined;
      navigate("/ThankYou", demoSummary ? { state: demoSummary } : undefined);
    }
    //console.log(JSON.stringify(tr, null, 2));
  }

  const handleReset = () => {
    setPath([]);
    tr.addState('restart');
  };

  const handleReverse = () => {
    if (path.length > 0) {
      tr.addState('undo');
      setPath(path.slice(0, -1));
    }
  };




  // converts numerical values to desired format for data base
  function numConvert(value) {
    if (Number(value) < 10) {
      return '0' + value;
    }
    return value;
  }

  const handleOnChange = (newPath) => {
    // new item installed in tr
    // if(newPath.length === 1){
    //   tr.addState('start');
    // }

    if (!fingerDownRef.current) {
      if (tr.countOfStats() === 0) {
        tr.addState('start');
      }
      tr.addState('fingerDown');
      fingerDownRef.current = true;
    }
    const tail = newPath.length - 1;
    const item = 'dot-' + numConvert(newPath[tail]);
    tr.addState(item);
    //path / current selected options updated
    setPath(newPath);
  };

  const handleFinish = () => {
    if (fingerDownRef.current) {
      fingerDownRef.current = false;
      tr.addState('fingerUp');
    }
    // console.log(path);
  };

  const toggleVideoGrid = () => {
    //demo video played
    tr.incDemoPlays();
    setShowVideo(!showVideo);
  };

  const onTouch = ({ touches }) => {
    if (showVideo) return;

    if (tr.countOfStats() === 0) {
      tr.addState('start');
    }
  }

  return (
    <div className="container">
      <div className='buttons'>
        <div className="left-buttons">
        </div>
        <div>
          <MemoizedImage
            showVideo={showVideo}
            levelAssets={levelAssets}
            levelName={levelDef["level"]}
            toggleAudio={memoizedToggleAudio}
            demoAudioRef={demoAudioRef}
            testAudioRef={testAudioRef}
          />
        </div>

        <div className='Level'></div>
        {!showVideo ? (
          <div className="right-button">
            <img alt="..."
              className='Reset'
              disabled={path.length === 0}
              onClick={handleReset} data-testid="reset_id"
              src={path.length === 0 ? commonAssets.disabledReset : commonAssets.resetButton}
              style={{ cursor: (path.length === 0) ? null : "pointer" }} />
            <img alt="..."
              className='Reverse'
              onClick={handleReverse}
              data-testid="reverse_id"
              src={path.length === 0 ? commonAssets.disabledUndo : commonAssets.undoButton}
              disabled={path.length === 0}
              style={{ cursor: (path.length === 0) ? null : "pointer" }} />

          </div>) : <div />}
      </div>

      <div className={levelDef['mode'] === 'practice' ? 'game_box_practice' : 'game_box'}>
        <div className='grids'>
          <div className="left_grid" data-testid="set_grid">
            <PatternLock
              width={grid_size}
              pointSize={point_size}
              path={showVideo ? resolved.leftGrid : set_pattern}
              size={gridDimension}
              disabled={true}
              onChange={handleOnChange}
              onFinish={handleFinish}
              allowOverlapping
              startDot={showVideo ? 0 : set_pattern[0]}// get the drawing start point
              animateLines={false}
            />
          </div>
          <div className={levelDef['mode'] === 'practice' ? 'divider_practice' : 'divider'}></div>
          <div className="right_grid" onTouchStart={onTouch} onClick={onTouch} data-testid="rightGrid">
            {showVideo ? (
              // <img src={videoSrc} controls loop autoPlay className='instructions_gif' alt="..."></img>
              <PatternLock
                gridSide="right"
                currentLevel={currentLevel}
                width={grid_size}
                pointSize={point_size}
                path={resolved.demoRight}
                disabled={true} // only disabled user input if we are showing the video
                size={gridDimension}
                allowOverlapping={false}
                allowJumping={false}
                startDot={resolved.demoRight[0]} // get the drawing start point
                animateLines={true} // only animaten lines if we are showing the video
              />
            ) : (
              <PatternLock
                gridSide="right"
                currentLevel={currentLevel}
                width={grid_size}
                pointSize={point_size}
                noPop={false}
                path={path}
                disabled={false} // only disabled user input if we are showing the video
                size={gridDimension}
                onChange={handleOnChange}
                onFinish={handleFinish}
                allowOverlapping={false}
                allowJumping={false}
                startDot={resolved.answer[0]} // get the drawing start point
                animateLines={false} // only animaten lines if we are showing the video
              />
            )}
          </div>
        </div>
      </div>

      {/* lower buttons, handling start, submit and watch again logic */}

      <div>
        {
          showVideo ?
            <div>
              <div className='bottomButtons'>
                <div className='cssButtonOutline'> start </div>
                <div className='cssButton' onClick={toggleVideoGrid} data-testid="toggle_id"> start </div>
              </div>
            </div>
            :
            <div className='bottomButtons'>
              <div className='cssButtonOutline'> done! </div>
              <div className='cssButton' disabled={showVideo} onClick={handleSubmit} data-testid="submit_id"> done! </div>
            </div>
        }
      </div>
      {isModalVisible && (
        <div className='modal'>
          <div className='modal_content'>
            <span className='close' onClick={closeModal}>&times;</span>
            <p>{errorMessage}</p>
          </div>
        </div>
      )}

      {/*
        VISCAT-386 (non-production only). There is otherwise no way off this page in
        demo mode: the only navigate('/') is closeModal, which is reachable solely
        through the POST-failure modal and that modal is gated on testUID. The app
        runs standalone/pseudo-fullscreen, so there is no browser back button either,
        and a tester who opens the wrong pattern would have to play the run out.
      */}
      {testToolsEnabled() && isDemoRun && (
        <button
          type="button"
          className='demo_exit_button'
          data-testid="demo_exit_id"
          onClick={exitDemoRun}
        >
          Exit
        </button>
      )}
    </div>
  );
};

export default VisCAT_test;
